import { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, IconButton, Tooltip } from '@material-ui/core';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import { getTone, greyAlpha, grey } from '../theme/tones';

/**
 * Remonte l'arbre jusqu'au premier ancêtre réellement défilant verticalement.
 * Utile quand la zone qui défile n'est pas celle qu'on rend soi-même (ici le
 * conteneur de contenu du DashboardLayout).
 */
export const findScrollParent = (node) => {
  let el = node ? node.parentElement : null;
  while (el) {
    const { overflowY } = window.getComputedStyle(el);
    if ((overflowY === 'auto' || overflowY === 'scroll') && el.scrollHeight > el.clientHeight + 4) {
      return el;
    }
    el = el.parentElement;
  }
  return null;
};

const BUTTON_SX = {
  width: 32,
  height: 32,
  bgcolor: grey(0),
  border: `1px solid ${greyAlpha(200, 0.9)}`,
  boxShadow: `0 4px 12px ${greyAlpha(900, 0.14)}`,
  transition: 'opacity 0.18s ease, background-color 0.18s ease'
};

/**
 * Deux flèches de défilement rapide : un clic saute en haut ou en bas de la
 * zone défilante, au lieu de la parcourir à la molette.
 *
 * `getTarget` renvoie l'élément qui défile. Il peut ne pas encore exister au
 * premier rendu (contenu monté après coup, Popover, Drawer…) : la résolution
 * est donc refaite à chaque changement de `watch`.
 *
 * `placement` :
 *  - `edges`  → une flèche collée en haut, l'autre en bas du bord droit ;
 *               adapté à un petit panneau (popover).
 *  - `corner` → les deux empilées dans le coin bas-droit ; adapté à une grande
 *               zone où l'on ne veut pas masquer le contenu du haut.
 */
const ScrollJumpButtons = ({ getTarget, watch, placement, accent, offset, fixed }) => {
  const [target, setTarget] = useState(null);
  const [state, setState] = useState({ up: false, down: false });

  const refresh = useCallback((el) => {
    if (!el) {
      setState({ up: false, down: false });
      return;
    }
    const { scrollTop, scrollHeight, clientHeight } = el;
    setState({
      up: scrollTop > 8,
      down: scrollTop + clientHeight < scrollHeight - 8
    });
  }, []);

  // La zone défilante est mesurée à la frame suivante : au moment du rendu,
  // sa hauteur réelle n'est pas encore connue.
  useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      const el = getTarget();
      setTarget(el || null);
      refresh(el);
    });
    return () => window.cancelAnimationFrame(frame);
  }, [getTarget, refresh, watch]);

  useEffect(() => {
    if (!target) return undefined;
    const onScroll = () => refresh(target);
    target.addEventListener('scroll', onScroll, { passive: true });
    return () => target.removeEventListener('scroll', onScroll);
  }, [target, refresh]);

  const scrollTo = (direction) => {
    if (!target) return;
    target.scrollTo({ top: direction === 'up' ? 0 : target.scrollHeight, behavior: 'smooth' });
  };

  // Rien à parcourir : on ne rend aucun bouton plutôt que deux boutons inertes.
  if (!state.up && !state.down) return null;

  const button = (direction) => {
    const visible = direction === 'up' ? state.up : state.down;
    const label = direction === 'up' ? 'Remonter en haut' : 'Descendre en bas';
    return (
      <Tooltip title={label}>
        <span>
          <IconButton
            onClick={() => scrollTo(direction)}
            size="small"
            aria-label={label}
            sx={{
              ...BUTTON_SX,
              color: accent,
              // Masqué plutôt que démonté : sans cela le second bouton se
              // déplacerait à chaque apparition ou disparition du premier.
              opacity: visible ? 1 : 0,
              pointerEvents: visible ? 'auto' : 'none',
              '&:hover': { bgcolor: `${accent}1A` }
            }}
          >
            {direction === 'up'
              ? <KeyboardArrowUpIcon sx={{ fontSize: 19 }} />
              : <KeyboardArrowDownIcon sx={{ fontSize: 19 }} />}
          </IconButton>
        </span>
      </Tooltip>
    );
  };

  // `fixed` quand la zone défilante est un ancêtre qu'on ne rend pas soi-même :
  // en `absolute` les boutons défileraient avec le contenu.
  const position = fixed ? 'fixed' : 'absolute';

  if (placement === 'edges') {
    return (
      <>
        <Box sx={{ position, right: offset, top: offset, zIndex: 3 }}>{button('up')}</Box>
        <Box sx={{ position, right: offset, bottom: offset, zIndex: 3 }}>{button('down')}</Box>
      </>
    );
  }

  return (
    <Box
      sx={{
        position,
        right: offset,
        bottom: offset,
        zIndex: 3,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.75
      }}
    >
      {button('up')}
      {button('down')}
    </Box>
  );
};

ScrollJumpButtons.propTypes = {
  /** Renvoie l'élément DOM défilant (peut renvoyer null tant qu'il n'existe pas). */
  getTarget: PropTypes.func.isRequired,
  /** Valeur dont le changement force une nouvelle mesure (contenu rechargé, onglet…). */
  watch: PropTypes.any,
  placement: PropTypes.oneOf(['edges', 'corner']),
  accent: PropTypes.string,
  offset: PropTypes.number,
  /** Positionne les boutons par rapport à la fenêtre plutôt qu'au parent. */
  fixed: PropTypes.bool
};

ScrollJumpButtons.defaultProps = {
  placement: 'corner',
  accent: getTone('indigo').base,
  offset: 8,
  fixed: false
};

export default ScrollJumpButtons;
